/**
 * Terms ending in the next 60 days.
 *
 * Each card is the term ONE contribution is in today: the product, the capital of
 * it still in, the days left (urgency-coloured) and the term's last day — as the
 * fund computes them. A term is followed by the next one by itself, so the list
 * never runs out while capital stays in. No amount is projected: no rate is
 * agreed, and the result of a term is the client's once the term is complete.
 * The canonical RiskDisclaimer closes the panel.
 */
import { formatMoney } from '../../lib/datetime';
import { colors } from '../../styles/theme';
import type { UpcomingMaturity } from '../../types/finance';
import RiskDisclaimer from '../RiskDisclaimer';
import {
  TERM_DATES_UNAVAILABLE,
  completeAfter,
  completeCountdown,
  daysUntilComplete,
  openTermKey,
  termsEndingWithin,
} from './openTerms.helpers';

const WINDOW_DAYS = 60;

interface Props {
  maturities: UpcomingMaturity[];
  /** False when the fund could not say every term's date. */
  available?: boolean;
}

function urgencyColor(days: number): string {
  if (days <= 7) return colors.danger;
  if (days <= 14) return colors.warning;
  return colors.success;
}

function MaturityRow({ m }: { readonly m: UpcomingMaturity }): JSX.Element {
  const days = daysUntilComplete(m.maturity_date);
  const urgency = urgencyColor(days);

  return (
    <div
      style={{
        padding: '14px 16px',
        background: colors.canvas,
        borderRadius: 12,
        border: `1px solid ${colors.borderSoft}`,
        display: 'grid',
        gap: 8,
      }}
    >
      {/* Row 1: product name */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <span
          style={{
            fontWeight: 600,
            fontSize: 13,
            color: colors.ink,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {m.product_name}
        </span>
      </div>

      {/* Row 2: principal + countdown */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 13, color: colors.muted }}>
          {formatMoney(m.amount, m.currency_code)}
        </span>
        <span style={{ fontSize: 12, fontWeight: 600, color: urgency }}>
          {completeCountdown(days)}
        </span>
      </div>

      {/* Row 3: the term's last day, as the day after which it is complete. No amount is
          projected: what the capital earns is the product's real result for the terms it
          completes. */}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <span style={{ fontSize: 11, color: colors.muted }}>
          {completeAfter(m.maturity_date)}
        </span>
      </div>
    </div>
  );
}

export default function MaturityForecast({ maturities, available = true }: Readonly<Props>): JSX.Element {
  const ending = termsEndingWithin(maturities, WINDOW_DAYS);
  return (
    <div
      style={{
        background: colors.surface,
        border: `1px solid ${colors.border}`,
        borderRadius: 16,
        padding: '20px 22px',
      }}
    >
      <p
        style={{
          margin: '0 0 16px 0',
          fontSize: 11,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.07em',
          color: colors.muted,
        }}
      >
        Terms ending · next 60 days
      </p>

      {!available && (
        <p style={{ margin: '0 0 10px 0', fontSize: 13, color: colors.muted }}>
          {TERM_DATES_UNAVAILABLE}
        </p>
      )}
      {ending.length === 0 && available && (
        <p style={{ margin: 0, fontSize: 13, color: colors.muted }}>
          No term ends in the next 60 days.
        </p>
      )}
      {ending.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {ending.map((m) => (
            <MaturityRow key={openTermKey(m)} m={m} />
          ))}
          <RiskDisclaimer compact extra="The amount shown is your capital in that term. The result of a term is yours once the term is complete, and the next term starts by itself." />
        </div>
      )}
    </div>
  );
}
